import React, { useCallback, useEffect, useMemo, useState } from "react";
import BN from "bn.js";
import { PublicKey } from "@solana/web3.js";
import { fromBaseUnits } from "@oxminter/shared/amounts";
import { parseRecipients, ParseResult, SAMPLE_CSV } from "../lib/csv";
import {
  ataFor,
  ataRentLamports,
  findMissingAtas,
  planBatches,
  PreparedBatch,
} from "../lib/token";
import { readTokenBalanceRaw, signAndSendWave } from "../lib/tx";
import {
  MAX_RECIPIENTS as SHARED_MAX_RECIPIENTS,
  TX_PER_SIGNING_WAVE,
  emptyJournal,
  journalMatches,
  journalSummary,
  planWaves,
  remaining,
  type DistributionJournal,
} from "@oxminter/shared/distribution";
import { useApp } from "../state";
import { Addr, Field, Note, RequireToken, RequireWallet } from "../components/common";
import { apiConfigured, recordEvent as postAuditEvent, sendDistributionAlert } from "../lib/api";
import {
  buildFailureReport,
  failureCsv,
  hasFailures,
  reportFilename,
  type FailedSend,
} from "@oxminter/shared/failures";

type RowState = "pending" | "sending" | "sent" | "failed";

interface Progress {
  byLine: Record<number, { state: RowState; sig?: string; error?: string }>;
  batchesDone: number;
  batchesTotal: number;
}

const MAX_RECIPIENTS = SHARED_MAX_RECIPIENTS;

/* The journal is keyed by token AND cluster: resuming one list against another
   list's journal would skip lines by NUMBER rather than by recipient, which is
   exactly how one wallet gets paid twice and another not at all. */
const journalKey = (mint: string, cluster: string) => `distribute-journal:${cluster}:${mint}`;

function loadJournal(key: string): DistributionJournal | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as DistributionJournal) : null;
  } catch {
    return null;
  }
}

function saveJournal(key: string, j: DistributionJournal): void {
  try {
    localStorage.setItem(key, JSON.stringify(j));
  } catch {
    /* private browsing: the run still works, it just cannot be resumed */
  }
}

export default function Distribute() {
  const { connection, net, wallet, token, fees, say } = useApp();
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [missing, setMissing] = useState<Set<string> | null>(null);
  const [batches, setBatches] = useState<PreparedBatch[] | null>(null);
  const [journal, setJournal] = useState<DistributionJournal | null>(null);
  /** What each recipient already holds of this mint, from the same account scan. */
  const [held, setHeld] = useState<Map<string, bigint>>(new Map());
  const [failureReport, setFailureReport] = useState<ReturnType<typeof buildFailureReport> | null>(null);
  const [rentLamports, setRentLamports] = useState<number | null>(null);
  const [balanceRaw, setBalanceRaw] = useState<bigint | null>(null);
  const [solLamports, setSolLamports] = useState<number | null>(null);
  const [confirm, setConfirm] = useState("");
  const [progress, setProgress] = useState<Progress | null>(null);
  const [busy, setBusy] = useState(false);

  const decimals = token?.decimals ?? 9;

  const refreshBalances = useCallback(async () => {
    if (!wallet || !token) return;
    const mint = new PublicKey(token.mint);
    setBalanceRaw(await readTokenBalanceRaw(connection, ataFor(mint, wallet.publicKey)));
    setSolLamports(await connection.getBalance(wallet.publicKey, "confirmed"));
    setRentLamports(await ataRentLamports(connection));
  }, [connection, wallet, token]);

  useEffect(() => {
    void refreshBalances();
  }, [refreshBalances]);

  const analyse = async () => {
    if (!wallet || !token) return;
    setBusy(true);
    setBatches(null);
    setProgress(null);
    try {
      const p = parseRecipients(text, decimals);
      setParsed(p);
      if (p.recipients.length === 0) {
        say("warn", "No valid recipients found.");
        return;
      }
      if (p.recipients.length > MAX_RECIPIENTS) {
        say(
          "error",
          `${p.recipients.length} recipients exceeds this tool's ${MAX_RECIPIENTS} limit. Split the list.`
        );
        return;
      }
      const mint = new PublicKey(token.mint);
      const { missing: miss, held } = await findMissingAtas(connection, mint, p.recipients);
      setMissing(miss);
      setHeld(held);
      setBatches(planBatches(mint, wallet.publicKey, decimals, p.recipients, miss));
      await refreshBalances();
      say(
        "ok",
        `${p.recipients.length} recipients, ${miss.size} need a new token account, ` +
          `${p.errors.length} rejected lines.`
      );
    } catch (e) {
      say("error", `Analysis failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const totals = useMemo(() => {
    if (!parsed || !batches || rentLamports === null) return null;
    const newAccounts = batches.reduce((a, b) => a + b.newAccounts, 0);
    const rent = newAccounts * rentLamports;
    // 5000 lamports base fee per signature; one signature per batch.
    const networkFee = batches.length * 5000;
    return {
      tokens: parsed.totalRaw,
      newAccounts,
      rentLamports: rent,
      networkFee,
      solNeeded: rent + networkFee,
    };
  }, [parsed, batches, rentLamports]);

  const blockers: string[] = [];
  if (parsed?.errors.length) blockers.push(`${parsed.errors.length} line(s) could not be parsed.`);
  if (parsed?.duplicates.length)
    blockers.push(`${parsed.duplicates.length} address(es) appear more than once.`);
  if (totals && balanceRaw !== null && totals.tokens > balanceRaw)
    blockers.push("The list sends more tokens than your account holds.");
  if (totals && solLamports !== null && totals.solNeeded > solLamports)
    blockers.push("Not enough SOL to cover account rent and network fees for this run.");

  async function send() {
    if (!wallet || !token || !batches) return;

    /* Skip what a previous run already paid. Every recipient in a batch shares
       one signature, so a batch is either wholly sent or wholly not — which is
       why filtering whole batches is exact rather than approximate. Getting
       this wrong pays people twice, so it is asserted in the test suite. */
    const jr = loadJournal(journalKey(token.mint, net.cluster));
    let todo =
      jr && journalMatches(jr, token.mint, net.cluster)
        ? batches.filter((b) => b.recipients.some((r) => jr.lines[r.line]?.state !== "sent"))
        : batches;

    /* The journal is written after a transaction confirms, so a tab closed in
       between leaves recipients paid on chain but unjournalled — and a cleared
       localStorage leaves everyone unjournalled. Reconcile against what they
       actually hold before sending: a recipient already holding exactly what
       this list would send them was paid by an earlier run, and sending again
       is a double payment that cannot be undone. Verified on a live validator:
       without this, a lost journal paid 361 recipients exactly twice. */
    const alreadyPaid = new Set<number>();
    const conflicting: { line: number; address: string }[] = [];
    for (const b of todo) {
      for (const r of b.recipients) {
        const balance = held.get(r.address);
        if (balance === undefined || balance === 0n) continue;
        if (balance === BigInt(r.raw.toString())) alreadyPaid.add(r.line);
        else conflicting.push({ line: r.line, address: r.address });
      }
    }
    if (alreadyPaid.size) {
      say(
        "warn",
        `${alreadyPaid.size} recipient(s) already hold exactly what this list sends them — ` +
          "treating them as paid by an earlier run and skipping."
      );
      todo = todo.filter((b) => b.recipients.some((r) => !alreadyPaid.has(r.line)));
    }
    if (conflicting.length) {
      say(
        "error",
        `${conflicting.length} recipient(s) already hold a DIFFERENT amount of this token ` +
          `(first: line ${conflicting[0].line}, ${conflicting[0].address}). Refusing to send: ` +
          "they may already have been paid, and it cannot be undone. Check them, then remove " +
          "them from the list or clear the run."
      );
      return;
    }

    if (!todo.length) {
      say("ok", "Every recipient on this list has already been paid. Nothing to send.");
      return;
    }
    if (todo.length < batches.length) {
      say(
        "info",
        `Resuming: ${batches.length - todo.length} of ${batches.length} transactions already landed.`
      );
    }
    await runBatches(todo);
  }

  /**
   * Send a set of batches, journalling every line as it resolves.
   *
   * Signed in waves rather than one transaction at a time: a wallet shows ONE
   * prompt for signAllTransactions, so a thousand recipients costs twenty
   * approvals instead of two hundred. Each wave takes a fresh blockhash,
   * because one shared across two hundred transactions would expire long
   * before the tail landed.
   */
  async function runBatches(toRun: PreparedBatch[]) {
    if (!wallet || !token) return;
    setBusy(true);

    const key = journalKey(token.mint, net.cluster);
    const existing = loadJournal(key);
    const jr =
      existing && journalMatches(existing, token.mint, net.cluster)
        ? existing
        : emptyJournal(token.mint, net.cluster, Date.now());

    const byLine: Progress["byLine"] = Object.fromEntries(
      (parsed?.recipients ?? []).map((r) => {
        const prior = jr.lines[r.line];
        return [
          r.line,
          prior?.state === "sent"
            ? { state: "sent" as RowState, sig: prior.sig }
            : { state: "pending" as RowState },
        ];
      })
    );
    let done = 0;
    setProgress({ byLine: { ...byLine }, batchesDone: 0, batchesTotal: toRun.length });

    const waves = planWaves(toRun, TX_PER_SIGNING_WAVE);
    for (const [w, wave] of waves.entries()) {
      wave.forEach((b) => b.recipients.forEach((r) => (byLine[r.line] = { state: "sending" })));
      setProgress({ byLine: { ...byLine }, batchesDone: done, batchesTotal: toRun.length });
      say("info", `Wave ${w + 1}/${waves.length}: one approval for ${wave.length} transaction(s)`);

      let outcomes: { signature?: string; error?: string }[];
      try {
        outcomes = await signAndSendWave({
          connection,
          wallet,
          fees,
          batches: wave,
          onProgress: (m) => say("info", m),
        });
      } catch (e) {
        // A declined approval fails the whole wave. The journal keeps whatever
        // earlier waves achieved, so the run resumes rather than being lost.
        const msg = (e as Error).message;
        outcomes = wave.map(() => ({ error: msg }));
        say("error", `Wave ${w + 1}/${waves.length} was not signed: ${msg}`);
      }

      wave.forEach((batch, i) => {
        const r = outcomes[i] ?? { error: "no result" };
        batch.recipients.forEach((rec) => {
          const state = r.signature
            ? { state: "sent" as RowState, sig: r.signature }
            : { state: "failed" as RowState, error: r.error };
          byLine[rec.line] = state;
          jr.lines[rec.line] = r.signature
            ? { state: "sent", sig: r.signature }
            : { state: "failed", error: r.error };
        });
        done++;
      });
      saveJournal(key, jr);
      setJournal({ ...jr });
      setProgress({ byLine: { ...byLine }, batchesDone: done, batchesTotal: toRun.length });
    }

    await refreshBalances();
    const sent = Object.values(byLine).filter((s) => s.state === "sent").length;
    const failed = Object.values(byLine).filter((s) => s.state === "failed").length;
    postAuditEvent({
      action: "distribute",
      cluster: net.cluster,
      mint: token?.mint,
      wallet: wallet?.publicKey.toBase58(),
      detail: `${sent} sent, ${failed} failed`,
    });
    /* The report exists whether or not anyone is signed in, and whether or not
       email is configured. It is the record; email is only the notification. */
    const failedSends: FailedSend[] = Object.entries(byLine)
      .filter(([, v]) => v.state === "failed")
      .map(([line, v]) => {
        const rec = parsed?.recipients.find((r) => r.line === Number(line));
        return {
          line: Number(line),
          address: rec?.address ?? "",
          amountRaw: rec?.raw.toString() ?? "",
          error: v.error ?? "unknown error",
        };
      });
    const report = buildFailureReport({
      cluster: net.cluster,
      mint: token.mint,
      symbol: token.symbol,
      wallet: wallet.publicKey.toBase58(),
      totalLines: (parsed?.recipients.length ?? 0) + (parsed?.errors.length ?? 0),
      sent,
      rejected: (parsed?.errors ?? []).map((e) => ({
        line: e.line,
        text: String(e.text ?? ""),
        reason: e.reason,
      })),
      failed: failedSends,
    });
    setFailureReport(hasFailures(report) ? report : null);

    if (hasFailures(report)) {
      if (!apiConfigured) {
        say("warn", "Download the failure report below — no account API is configured to email it.");
      } else {
        const alert = await sendDistributionAlert(report);
        if (alert?.emailed) say("ok", `Failure report emailed to ${alert.to}.`);
        else if (alert)
          say("warn", `Failure report recorded but not emailed: ${alert.reason ?? "unknown reason"}`);
        else say("warn", "Could not reach the API to send the failure alert. The report is below.");
      }
    }

    say(failed ? "warn" : "ok", `Distribution finished: ${sent} sent, ${failed} failed.`);
    setBusy(false);
  }

  /* Read the journal for THIS token and cluster, so the panel can say what a
     previous run achieved before anything is sent again. */
  const resumeSummary = useMemo(() => {
    if (!token || !parsed) return null;
    const j = loadJournal(journalKey(token.mint, net.cluster));
    if (!j || !journalMatches(j, token.mint, net.cluster)) return null;
    const { sent, failed } = journalSummary(j);
    const todo = remaining(
      parsed.recipients.map((r) => ({
        line: r.line,
        address: r.address,
        raw: r.raw.toString(),
        needsAccount: false,
      })),
      j
    ).length;
    return { sent, failed, todo };
  }, [token, parsed, net.cluster, journal]);

  const ready = batches !== null && blockers.length === 0 && confirm === "SEND" && !busy;

  return (
    <RequireWallet>
      <RequireToken>
        <div className="card">
          <h2>Distribute {token!.symbol}</h2>
          <p className="sub">
            Batched SPL transfers to a list of wallets. Every address is validated, duplicates and
            unparseable lines block the run, and the SOL cost of creating accounts for new holders
            is shown before you approve anything.
          </p>

          <Note>
            <p>
              Recipients who have never held this token need an associated token account, and{" "}
              <strong>you</strong> pay the rent for each one — roughly{" "}
              {rentLamports ? fromBaseUnits(new BN(rentLamports), 9) : "0.002"} SOL per new holder.
              For 500 fresh wallets that is real money, so it is priced up front rather than
              discovered halfway through.
            </p>
          </Note>

          <Field
            label="Recipients"
            hint="One per line: address,amount. Comma, semicolon or tab separated. Header row and # comments allowed."
          >
            <textarea
              data-testid="in-csv"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setBatches(null);
                setProgress(null);
              }}
              placeholder={SAMPLE_CSV}
            />
          </Field>

          <div className="row">
            <button onClick={analyse} disabled={busy || !text.trim()} data-testid="btn-analyse">
              {busy && !progress ? "Checking…" : "Check list"}
            </button>
            <label className="row" style={{ gap: 6 }}>
              <input
                type="file"
                accept=".csv,.txt"
                style={{ display: "none" }}
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (f) setText(await f.text());
                }}
                data-testid="in-csvfile"
                id="csvfile"
              />
              <button onClick={() => document.getElementById("csvfile")?.click()}>
                Load CSV file
              </button>
            </label>
          </div>
        </div>

        {parsed ? (
          <div className="card">
            <h2>Review</h2>
            <dl className="kv">
              <dt>Valid recipients</dt>
              <dd data-testid="dist-count">{parsed.recipients.length}</dd>
              <dt>Total to send</dt>
              <dd data-testid="dist-total">
                {fromBaseUnits(new BN(parsed.totalRaw.toString()), decimals)} {token!.symbol}
              </dd>
              <dt>Your balance</dt>
              <dd>
                {balanceRaw === null
                  ? "—"
                  : `${fromBaseUnits(new BN(balanceRaw.toString()), decimals)} ${token!.symbol}`}
              </dd>
              <dt>New token accounts</dt>
              <dd>{totals?.newAccounts ?? "—"}</dd>
              <dt>SOL cost (rent + fees)</dt>
              <dd data-testid="dist-solcost">
                {totals ? fromBaseUnits(new BN(totals.solNeeded), 9) : "—"} SOL
              </dd>
              <dt>Transactions</dt>
              <dd data-testid="dist-batches">{batches?.length ?? "—"}</dd>
              <dt>Wallet approvals</dt>
              <dd data-testid="dist-approvals">
                {batches ? Math.ceil(batches.length / TX_PER_SIGNING_WAVE) : "—"}
              </dd>
            </dl>

            {batches && batches.length > TX_PER_SIGNING_WAVE ? (
              <Note kind="warn">
                <p data-testid="dist-scale-warning">
                  This is a large run: <strong>{batches.length} transactions</strong> in{" "}
                  <strong>{Math.ceil(batches.length / TX_PER_SIGNING_WAVE)} wallet approvals</strong>,
                  taking several minutes. Each approval covers up to {TX_PER_SIGNING_WAVE}{" "}
                  transactions at once.
                </p>
                <p>
                  {totals?.newAccounts
                    ? `You pay ${fromBaseUnits(new BN(totals.rentLamports), 9)} SOL of rent for ${totals.newAccounts} new holders, and it is not refundable. `
                    : ""}
                  Progress is saved as it goes: if you close this tab or decline an approval, come
                  back and the run resumes without re-sending anyone.
                </p>
                <p>
                  For runs at this size the CLI is the better tool —{" "}
                  <code>launch.js distribute</code> signs with a local keypair, so there are no
                  approvals at all, and it resumes from its own journal.
                </p>
              </Note>
            ) : null}

            {resumeSummary && resumeSummary.sent > 0 ? (
              <Note kind="ok">
                <p data-testid="dist-resume">
                  A previous run of this list sent <strong>{resumeSummary.sent}</strong> recipients
                  {resumeSummary.failed ? ` and failed on ${resumeSummary.failed}` : ""}. Sending
                  again skips everyone already paid and retries the rest —{" "}
                  <strong>{resumeSummary.todo}</strong> left.
                </p>
                <button
                  className="ghost"
                  data-testid="btn-forget-journal"
                  onClick={() => {
                    if (!token) return;
                    try {
                      localStorage.removeItem(journalKey(token.mint, net.cluster));
                    } catch {
                      /* nothing to forget */
                    }
                    setJournal(null);
                    say("warn", "Forgot the previous run. The next send treats every line as unpaid.");
                  }}
                >
                  Forget that run
                </button>
              </Note>
            ) : null}

            {failureReport ? (
              <Note kind="danger">
                <p data-testid="failure-report">
                  <strong>
                    {failureReport.rejected.length} rejected before sending
                    {failureReport.failed.length
                      ? `, ${failureReport.failed.length} transfer(s) failed`
                      : ""}
                    .
                  </strong>{" "}
                  Rejected lines never left this machine — no tokens moved and no fees were spent
                  on them. Failed transfers are retried by running the same list again; anyone
                  already paid is skipped.
                </p>
                <div className="row" style={{ marginTop: 8 }}>
                  <button
                    data-testid="btn-download-failures"
                    onClick={() => {
                      const csv = failureCsv(failureReport);
                      const blob = new Blob([csv], { type: "text/csv" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = reportFilename(failureReport, "csv");
                      a.style.display = "none";
                      document.body.appendChild(a);
                      a.click();
                      setTimeout(() => {
                        URL.revokeObjectURL(url);
                        a.remove();
                      }, 30_000);
                    }}
                  >
                    Download failure report (CSV)
                  </button>
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>
                    Same columns as the import format, so a corrected file goes straight back in.
                  </span>
                </div>
              </Note>
            ) : null}

            {parsed.errors.length ? (
              <>
                <h3>Rejected lines</h3>
                <div className="scroll">
                  <table data-testid="dist-errors">
                    <thead>
                      <tr>
                        <th>Line</th>
                        <th>Content</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.errors.map((e, i) => (
                        <tr key={i}>
                          <td>{e.line}</td>
                          <td className="mono">{e.text}</td>
                          <td>{e.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}

            {parsed.duplicates.length ? (
              <Note kind="danger">
                <p>Duplicate addresses would be paid twice:</p>
                {parsed.duplicates.map((d) => (
                  <p key={d.address} className="mono">
                    {d.address} on lines {d.lines.join(", ")}
                  </p>
                ))}
              </Note>
            ) : null}

            {blockers.length ? (
              <Note kind="danger">
                {blockers.map((b, i) => (
                  <p key={i}>{b}</p>
                ))}
                <p>Fix the list and check it again — nothing will be sent while any of these stand.</p>
              </Note>
            ) : null}

            {batches && blockers.length === 0 ? (
              <>
                <Note kind="warn">
                  <p>
                    This will send{" "}
                    <strong>
                      {fromBaseUnits(new BN(parsed.totalRaw.toString()), decimals)}{" "}
                      {token!.symbol}
                    </strong>{" "}
                    to <strong>{parsed.recipients.length}</strong> wallets across{" "}
                    {batches.length} transactions. Your wallet will prompt once per transaction.
                    Transfers cannot be reversed.
                  </p>
                </Note>
                <Field label="Type SEND to confirm">
                  <input
                    data-testid="in-sendconfirm"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="SEND"
                  />
                </Field>
                <button className="danger" onClick={send} disabled={!ready} data-testid="btn-send">
                  {busy && progress ? "Sending…" : "Send"}
                </button>
              </>
            ) : null}
          </div>
        ) : null}

        {progress ? (
          <div className="card">
            <h2>Progress</h2>
            <div className="bar" style={{ marginBottom: 12 }}>
              <i style={{ width: `${(progress.batchesDone / progress.batchesTotal) * 100}%` }} />
            </div>
            <p className="sub">
              {progress.batchesDone}/{progress.batchesTotal} transactions
            </p>
            <div className="scroll">
              <table data-testid="dist-progress">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th className="mono">Address</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Tx</th>
                  </tr>
                </thead>
                <tbody>
                  {(parsed?.recipients ?? []).map((r) => {
                    const s = progress.byLine[r.line];
                    const cls =
                      s?.state === "sent" ? "ok" : s?.state === "failed" ? "danger" : "warn";
                    return (
                      <tr key={r.line}>
                        <td>{r.line}</td>
                        <td className="mono">
                          {r.address.slice(0, 6)}…{r.address.slice(-6)}
                          {missing?.has(r.address) ? " (new)" : ""}
                        </td>
                        <td>{r.amount}</td>
                        <td>
                          <span className={`pill ${cls}`}>{s?.state ?? "pending"}</span>
                        </td>
                        <td>{s?.sig ? <Addr value={s.sig} kind="tx" /> : null}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </RequireToken>
    </RequireWallet>
  );
}
